import { ModuleStoreState, type ModuleStoreCard, type ProductCode } from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { adminTableFrameClassName } from '@/platform/features/admin-panel';
import {
  moduleNeedsUpgrade,
  moduleStoreStatusLabel,
} from '@/platform/features/modules/module-store-status';
import {
  useInstallModule,
  useModuleStore,
  useRemoveModule,
  useUpgradeModule,
} from '@/platform/features/modules/use-module-store';

export function ModulesAdminPage() {
  usePageTitle('Module');
  const catalog = useModuleStore();
  const install = useInstallModule();
  const remove = useRemoveModule();
  const upgrade = useUpgradeModule();
  const [pendingRemove, setPendingRemove] = useState<ModuleStoreCard | null>(null);
  const busy = install.isPending || remove.isPending || upgrade.isPending;

  async function runInstall(code: ProductCode, label: string) {
    try {
      await install.mutateAsync(code);
      toast.success(`${label} ist installiert.`);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Die Installation ist fehlgeschlagen.',
      );
    }
  }

  async function runUpgrade(code: ProductCode, label: string) {
    try {
      await upgrade.mutateAsync(code);
      toast.success(`${label} ist aktualisiert.`);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Die Aktualisierung ist fehlgeschlagen.',
      );
    }
  }

  async function runRemove() {
    if (!pendingRemove) {
      return;
    }
    try {
      await remove.mutateAsync(pendingRemove.code);
      toast.success(`${pendingRemove.labelDe} ist entfernt. Die Daten bleiben erhalten.`);
      setPendingRemove(null);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Das Entfernen ist fehlgeschlagen.',
      );
    }
  }

  return (
    <section className="space-y-6">
      <PlatformPageHeader
        title="Module"
        description="Fachmodule dieses Mandanten installieren, aktualisieren oder entfernen. Entfernte Daten bleiben erhalten."
      />

      {catalog.isPending && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Module werden geladen …
        </p>
      )}

      {catalog.isError && (
        <p role="alert" className="text-sm text-destructive">
          {catalog.error instanceof ApiRequestError
            ? catalog.error.message
            : 'Die Module konnten nicht geladen werden.'}
        </p>
      )}

      {catalog.data && (
        <ul className={`${adminTableFrameClassName} divide-y divide-border`}>
          {catalog.data.items.map((card) => (
            <li
              key={card.code}
              className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{card.labelDe}</span>
                  {card.state === ModuleStoreState.PLANNED ? (
                    <Badge variant="secondary">Demnächst</Badge>
                  ) : null}
                  {card.state === ModuleStoreState.INSTALLED ? (
                    <Badge variant="secondary">Installiert</Badge>
                  ) : null}
                  {card.state === ModuleStoreState.REMOVED ? (
                    <Badge variant="outline">Entfernt</Badge>
                  ) : null}
                </div>
                <p className="text-sm text-muted-foreground">{card.descriptionDe}</p>
                <p className="text-xs text-muted-foreground">{moduleStoreStatusLabel(card)}</p>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                {card.installable ? (
                  <Button
                    type="button"
                    size="sm"
                    disabled={busy}
                    onClick={() => void runInstall(card.code, card.labelDe)}
                  >
                    Installieren
                  </Button>
                ) : null}
                {moduleNeedsUpgrade(card) ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => void runUpgrade(card.code, card.labelDe)}
                  >
                    Aktualisieren
                  </Button>
                ) : null}
                {card.state === ModuleStoreState.INSTALLED ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => setPendingRemove(card)}
                  >
                    Entfernen
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      <AlertDialog
        open={pendingRemove !== null}
        onOpenChange={(open) => {
          if (!open && !remove.isPending) {
            setPendingRemove(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingRemove?.labelDe} entfernen?</AlertDialogTitle>
            <AlertDialogDescription>
              Die Einstiege verschwinden aus der Verwaltung. Die Daten bleiben erhalten und sind
              nach erneutem Installieren wieder erreichbar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={remove.isPending}
              onClick={(event) => {
                event.preventDefault();
                void runRemove();
              }}
            >
              {remove.isPending ? 'Wird entfernt …' : 'Entfernen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
